// src/components/ComparePage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, CircularProgress, IconButton,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────

const isEmpty = (v) => v === null || v === undefined || v === '';

/**
 * Riga di confronto. Ritorna null se entrambi i valori sono vuoti (non mostra righe N/A N/A).
 * Evidenzia in giallo se i valori differiscono.
 */
const CompareRow = ({ label, valA, valB, unit = '' }) => {
  if (isEmpty(valA) && isEmpty(valB)) return null;

  const a = isEmpty(valA) ? '—' : `${valA}${unit ? ' ' + unit : ''}`;
  const b = isEmpty(valB) ? '—' : `${valB}${unit ? ' ' + unit : ''}`;
  const different = a !== b;

  return (
    <TableRow sx={{
      backgroundColor: different ? 'rgba(255,193,7,0.10)' : 'transparent',
      '&:last-child td': { border: 0 },
    }}>
      <TableCell component="th" scope="row" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>
        {label}
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: different ? 700 : 400, color: different ? 'warning.main' : 'text.primary' }}>
        {a}
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: different ? 700 : 400, color: different ? 'warning.main' : 'text.primary' }}>
        {b}
      </TableCell>
    </TableRow>
  );
};

/** Riga speciale per il campo Toe (valore + direzione in/out). */
const ToeRow = ({ label, valA, valB, t }) => {
  if (isEmpty(valA?.val) && isEmpty(valB?.val)) return null;

  const fmt = (v) => {
    if (isEmpty(v?.val)) return '—';
    const dir = v.dir ? t(`field_toe_${v.dir}`, { defaultValue: v.dir }) : '';
    return `${v.val} mm ${dir}`;
  };

  const a = fmt(valA);
  const b = fmt(valB);
  const different = a !== b;

  return (
    <TableRow sx={{
      backgroundColor: different ? 'rgba(255,193,7,0.10)' : 'transparent',
      '&:last-child td': { border: 0 },
    }}>
      <TableCell component="th" scope="row" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>
        {label}
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: different ? 700 : 400, color: different ? 'warning.main' : 'text.primary' }}>
        {a}
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: different ? 700 : 400, color: different ? 'warning.main' : 'text.primary' }}>
        {b}
      </TableCell>
    </TableRow>
  );
};

/** Header di sezione con tag angolo (FL/FR/RL/RR) o testo semplice. */
const SectionHeader = ({ tag, label }) => (
  <TableRow sx={{ backgroundColor: 'background.default' }}>
    <TableCell colSpan={3} sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {tag && (
          <Typography fontWeight={800} fontSize={15} color="text.primary" lineHeight={1}>
            {tag}
          </Typography>
        )}
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          {label}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

// ─────────────────────────────────────────────────────────────────
// COMPARE PAGE
// ─────────────────────────────────────────────────────────────────

function ComparePage({ teamId }) {
  const { t, i18n } = useTranslation('setup');
  const currentLang = i18n.language;
  const { setupId1, setupId2 } = useParams();

  const [setupA, setSetupA] = useState(null);
  const [setupB, setSetupB] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBoth = async () => {
      setIsLoading(true);
      try {
        const [snapA, snapB] = await Promise.all([
          getDoc(doc(db, 'setupSheets', setupId1)),
          getDoc(doc(db, 'setupSheets', setupId2)),
        ]);
        if (
          snapA.exists() && snapB.exists() &&
          snapA.data().teamId === teamId && snapB.data().teamId === teamId
        ) {
          setSetupA(snapA.data());
          setSetupB(snapB.data());
        }
      } catch (err) {
        console.error('ComparePage load error:', err);
      }
      setIsLoading(false);
    };
    fetchBoth();
  }, [setupId1, setupId2, teamId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!setupA || !setupB) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h4" color="error">{t('compare.error_loading')}</Typography>
        <Button component={RouterLink} to="/" sx={{ mt: 2 }} variant="contained">
          {t('compare.back_button')}
        </Button>
      </Box>
    );
  }

  const dA = setupA.setupData || {};
  const dB = setupB.setupData || {};
  const locale = currentLang === 'it' ? 'it-IT' : 'en-GB';
  const fmtDate = (d) => d ? new Date(d.toDate()).toLocaleDateString(locale) : '—';

  // Helper inline per i dati ammortizzatori
  const amA = (axle) => dA[axle]?.ammortizzatori || {};
  const amB = (axle) => dB[axle]?.ammortizzatori || {};

  return (
    <>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton component={RouterLink} to={`/vehicle/${setupA.vehicleId}`} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>{t('compare.title')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={setupA.sessionName || t('compare.header_setup_a')} size="small" />
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>vs</Typography>
          <Chip label={setupB.sessionName || t('compare.header_setup_b')} size="small" />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '34%', fontWeight: 700 }}>{t('compare.header_parameter')}</TableCell>
                <TableCell align="center" sx={{ width: '33%', fontWeight: 700 }}>
                  {setupA.sessionName || t('compare.header_setup_a')}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {fmtDate(setupA.dataSessione)}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '33%', fontWeight: 700 }}>
                  {setupB.sessionName || t('compare.header_setup_b')}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {fmtDate(setupB.dataSessione)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>

              {/* ══ SESSIONE ══ */}
              <SectionHeader label={t('section_session_info')} />
              <CompareRow label={t('field_event')}      valA={setupA.eventName}          valB={setupB.eventName} />
              <CompareRow label={t('field_driver')}     valA={setupA.nomePilota}          valB={setupB.nomePilota} />
              <CompareRow label={t('field_air_temp')}   valA={dA.meteo?.tempAria}         valB={dB.meteo?.tempAria}    unit="°C" />
              <CompareRow label={t('field_track_temp')} valA={dA.meteo?.tempAsfalto}      valB={dB.meteo?.tempAsfalto} unit="°C" />
              <CompareRow label={t('field_humidity')}   valA={dA.meteo?.umidita}          valB={dB.meteo?.umidita}     unit="%" />

              {/* ══ FL ══ */}
              <SectionHeader tag="FL" label={t('quadrant_fl')} />
              <ToeRow label={t('cc_label_toe')}
                valA={{ val: dA.anteriore?.convergenzaSX, dir: dA.anteriore?.toeInOrOutSX }}
                valB={{ val: dB.anteriore?.convergenzaSX, dir: dB.anteriore?.toeInOrOutSX }} t={t} />
              <CompareRow label={t('cc_label_camber')}       valA={dA.anteriore?.camberSX}               valB={dB.anteriore?.camberSX}               unit="deg" />
              <CompareRow label={t('cc_label_caster')}       valA={dA.anteriore?.casterSX}               valB={dB.anteriore?.casterSX}               unit="deg" />
              <CompareRow label={t('cc_label_height')}       valA={dA.aero?.altezzaTargetSX}             valB={dB.aero?.altezzaTargetSX}             unit="mm" />
              <CompareRow label={t('cc_label_spring')}       valA={dA.anteriore?.mollaSX}                valB={dB.anteriore?.mollaSX} />
              <CompareRow label={t('cc_label_bs_rate')}      valA={dA.anteriore?.rigidezzaBumpstopSX}    valB={dB.anteriore?.rigidezzaBumpstopSX} />
              <CompareRow label={t('cc_label_bs_range')}     valA={dA.anteriore?.distanzaBumpstopSX}     valB={dB.anteriore?.distanzaBumpstopSX}     unit="mm" />
              <CompareRow label={t('cc_label_bump_slow')}    valA={amA('anteriore').bumpSX}              valB={amB('anteriore').bumpSX} />
              <CompareRow label={t('cc_label_rebound_slow')} valA={amA('anteriore').reboundSX}           valB={amB('anteriore').reboundSX} />
              <CompareRow label={t('cc_label_bump_fast')}    valA={amA('anteriore').fastBumpSX}          valB={amB('anteriore').fastBumpSX} />
              <CompareRow label={t('cc_label_rebound_fast')} valA={amA('anteriore').fastReboundSX}       valB={amB('anteriore').fastReboundSX} />
              <CompareRow label={t('cc_label_p_cold')}       valA={dA.gomme_ant_sx?.pFreddo}             valB={dB.gomme_ant_sx?.pFreddo}             unit="psi" />
              <CompareRow label={t('cc_label_p_hot')}        valA={dA.gomme_ant_sx?.pCaldoTarget}        valB={dB.gomme_ant_sx?.pCaldoTarget}        unit="psi" />
              <CompareRow label={t('field_tire_code')}       valA={dA.gomme_ant_sx?.codicePneumatico}    valB={dB.gomme_ant_sx?.codicePneumatico} />
              <CompareRow label={t('cc_corner_weight')}      valA={dA.weights?.cornerFL}                 valB={dB.weights?.cornerFL}                 unit="kg" />

              {/* ══ FR ══ */}
              <SectionHeader tag="FR" label={t('quadrant_fr')} />
              <ToeRow label={t('cc_label_toe')}
                valA={{ val: dA.anteriore?.convergenzaDX, dir: dA.anteriore?.toeInOrOutDX }}
                valB={{ val: dB.anteriore?.convergenzaDX, dir: dB.anteriore?.toeInOrOutDX }} t={t} />
              <CompareRow label={t('cc_label_camber')}       valA={dA.anteriore?.camberDX}               valB={dB.anteriore?.camberDX}               unit="deg" />
              <CompareRow label={t('cc_label_caster')}       valA={dA.anteriore?.casterDX}               valB={dB.anteriore?.casterDX}               unit="deg" />
              <CompareRow label={t('cc_label_height')}       valA={dA.aero?.altezzaTargetDX}             valB={dB.aero?.altezzaTargetDX}             unit="mm" />
              <CompareRow label={t('cc_label_spring')}       valA={dA.anteriore?.mollaDX}                valB={dB.anteriore?.mollaDX} />
              <CompareRow label={t('cc_label_bs_rate')}      valA={dA.anteriore?.rigidezzaBumpstopDX}    valB={dB.anteriore?.rigidezzaBumpstopDX} />
              <CompareRow label={t('cc_label_bs_range')}     valA={dA.anteriore?.distanzaBumpstopDX}     valB={dB.anteriore?.distanzaBumpstopDX}     unit="mm" />
              <CompareRow label={t('cc_label_bump_slow')}    valA={amA('anteriore').bumpDX}              valB={amB('anteriore').bumpDX} />
              <CompareRow label={t('cc_label_rebound_slow')} valA={amA('anteriore').reboundDX}           valB={amB('anteriore').reboundDX} />
              <CompareRow label={t('cc_label_bump_fast')}    valA={amA('anteriore').fastBumpDX}          valB={amB('anteriore').fastBumpDX} />
              <CompareRow label={t('cc_label_rebound_fast')} valA={amA('anteriore').fastReboundDX}       valB={amB('anteriore').fastReboundDX} />
              <CompareRow label={t('cc_label_p_cold')}       valA={dA.gomme_ant_dx?.pFreddo}             valB={dB.gomme_ant_dx?.pFreddo}             unit="psi" />
              <CompareRow label={t('cc_label_p_hot')}        valA={dA.gomme_ant_dx?.pCaldoTarget}        valB={dB.gomme_ant_dx?.pCaldoTarget}        unit="psi" />
              <CompareRow label={t('field_tire_code')}       valA={dA.gomme_ant_dx?.codicePneumatico}    valB={dB.gomme_ant_dx?.codicePneumatico} />
              <CompareRow label={t('cc_corner_weight')}      valA={dA.weights?.cornerFR}                 valB={dB.weights?.cornerFR}                 unit="kg" />

              {/* ══ RL ══ */}
              <SectionHeader tag="RL" label={t('quadrant_rl')} />
              <ToeRow label={t('cc_label_toe')}
                valA={{ val: dA.posteriore?.convergenzaSX, dir: dA.posteriore?.toeInOrOutSX }}
                valB={{ val: dB.posteriore?.convergenzaSX, dir: dB.posteriore?.toeInOrOutSX }} t={t} />
              <CompareRow label={t('cc_label_camber')}       valA={dA.posteriore?.camberSX}              valB={dB.posteriore?.camberSX}              unit="deg" />
              <CompareRow label={t('cc_label_spring')}       valA={dA.posteriore?.mollaSX}               valB={dB.posteriore?.mollaSX} />
              <CompareRow label={t('cc_label_bs_rate')}      valA={dA.posteriore?.rigidezzaBumpstopSX}   valB={dB.posteriore?.rigidezzaBumpstopSX} />
              <CompareRow label={t('cc_label_bs_range')}     valA={dA.posteriore?.distanzaBumpstopSX}    valB={dB.posteriore?.distanzaBumpstopSX}    unit="mm" />
              <CompareRow label={t('cc_label_bump_slow')}    valA={amA('posteriore').bumpSX}             valB={amB('posteriore').bumpSX} />
              <CompareRow label={t('cc_label_rebound_slow')} valA={amA('posteriore').reboundSX}          valB={amB('posteriore').reboundSX} />
              <CompareRow label={t('cc_label_bump_fast')}    valA={amA('posteriore').fastBumpSX}         valB={amB('posteriore').fastBumpSX} />
              <CompareRow label={t('cc_label_rebound_fast')} valA={amA('posteriore').fastReboundSX}      valB={amB('posteriore').fastReboundSX} />
              <CompareRow label={t('cc_label_p_cold')}       valA={dA.gomme_post_sx?.pFreddo}            valB={dB.gomme_post_sx?.pFreddo}            unit="psi" />
              <CompareRow label={t('cc_label_p_hot')}        valA={dA.gomme_post_sx?.pCaldoTarget}       valB={dB.gomme_post_sx?.pCaldoTarget}       unit="psi" />
              <CompareRow label={t('field_tire_code')}       valA={dA.gomme_post_sx?.codicePneumatico}   valB={dB.gomme_post_sx?.codicePneumatico} />
              <CompareRow label={t('cc_corner_weight')}      valA={dA.weights?.cornerRL}                 valB={dB.weights?.cornerRL}                 unit="kg" />

              {/* ══ RR ══ */}
              <SectionHeader tag="RR" label={t('quadrant_rr')} />
              <ToeRow label={t('cc_label_toe')}
                valA={{ val: dA.posteriore?.convergenzaDX, dir: dA.posteriore?.toeInOrOutDX }}
                valB={{ val: dB.posteriore?.convergenzaDX, dir: dB.posteriore?.toeInOrOutDX }} t={t} />
              <CompareRow label={t('cc_label_camber')}       valA={dA.posteriore?.camberDX}              valB={dB.posteriore?.camberDX}              unit="deg" />
              <CompareRow label={t('cc_label_spring')}       valA={dA.posteriore?.mollaDX}               valB={dB.posteriore?.mollaDX} />
              <CompareRow label={t('cc_label_bs_rate')}      valA={dA.posteriore?.rigidezzaBumpstopDX}   valB={dB.posteriore?.rigidezzaBumpstopDX} />
              <CompareRow label={t('cc_label_bs_range')}     valA={dA.posteriore?.distanzaBumpstopDX}    valB={dB.posteriore?.distanzaBumpstopDX}    unit="mm" />
              <CompareRow label={t('cc_label_bump_slow')}    valA={amA('posteriore').bumpDX}             valB={amB('posteriore').bumpDX} />
              <CompareRow label={t('cc_label_rebound_slow')} valA={amA('posteriore').reboundDX}          valB={amB('posteriore').reboundDX} />
              <CompareRow label={t('cc_label_bump_fast')}    valA={amA('posteriore').fastBumpDX}         valB={amB('posteriore').fastBumpDX} />
              <CompareRow label={t('cc_label_rebound_fast')} valA={amA('posteriore').fastReboundDX}      valB={amB('posteriore').fastReboundDX} />
              <CompareRow label={t('cc_label_p_cold')}       valA={dA.gomme_post_dx?.pFreddo}            valB={dB.gomme_post_dx?.pFreddo}            unit="psi" />
              <CompareRow label={t('cc_label_p_hot')}        valA={dA.gomme_post_dx?.pCaldoTarget}       valB={dB.gomme_post_dx?.pCaldoTarget}       unit="psi" />
              <CompareRow label={t('field_tire_code')}       valA={dA.gomme_post_dx?.codicePneumatico}   valB={dB.gomme_post_dx?.codicePneumatico} />
              <CompareRow label={t('cc_corner_weight')}      valA={dA.weights?.cornerRR}                 valB={dB.weights?.cornerRR}                 unit="kg" />

              {/* ══ TELAIO ══ */}
              <SectionHeader label={t('cc_tab_chassis')} />
              <CompareRow label={t('cc_arb_front')}        valA={dA.anteriore?.barra}          valB={dB.anteriore?.barra} />
              <CompareRow label={t('cc_arb_rear')}         valA={dA.posteriore?.barra}         valB={dB.posteriore?.barra} />
              <CompareRow label={t('field_diff_preload')}  valA={dA.differenziale?.precarico}  valB={dB.differenziale?.precarico}  unit="Nm" />
              <CompareRow label={t('field_diff_lock')}     valA={dA.differenziale?.bloccaggio} valB={dB.differenziale?.bloccaggio} unit="%" />
              <CompareRow label={t('field_brake_bias')}    valA={dA.freni?.bilanciamentoFreni} valB={dB.freni?.bilanciamentoFreni} unit="%" />
              <CompareRow label={t('field_brake_power')}   valA={dA.freni?.potenzaFreni}       valB={dB.freni?.potenzaFreni}       unit="%" />
              <CompareRow label={t('field_steering_ratio')} valA={dA.freni?.rapportoSterzo}    valB={dB.freni?.rapportoSterzo} />

              {/* ══ AERO ══ */}
              <SectionHeader label={t('section_aero')} />
              <CompareRow label={t('field_wing_rear')}         valA={dA.aero?.alaPosteriore}           valB={dB.aero?.alaPosteriore} />
              <CompareRow label={t('field_splitter')}          valA={dA.aero?.splitterAnteriore}       valB={dB.aero?.splitterAnteriore} />
              <CompareRow label={t('field_brake_ducts_front')} valA={dA.aero?.condottiFrenoAnteriori}  valB={dB.aero?.condottiFrenoAnteriori} />
              <CompareRow label={t('field_brake_ducts_rear')}  valA={dA.aero?.condottiFrenoPosteriori} valB={dB.aero?.condottiFrenoPosteriori} />

              {/* ══ ELETTRONICA ══ */}
              <SectionHeader label={t('section_electronics')} />
              <CompareRow label={t('field_electronics_tc1')} valA={dA.elettronica?.tc1}      valB={dB.elettronica?.tc1} />
              <CompareRow label={t('field_electronics_tc2')} valA={dA.elettronica?.tc2}      valB={dB.elettronica?.tc2} />
              <CompareRow label={t('field_electronics_abs')} valA={dA.elettronica?.abs}      valB={dB.elettronica?.abs} />
              <CompareRow label={t('field_electronics_ecu')} valA={dA.elettronica?.mappaECU} valB={dB.elettronica?.mappaECU} />

              {/* ══ PESI ══ */}
              <SectionHeader label={t('cc_tab_weights')} />
              <CompareRow label={t('field_dry_weight')}     valA={dA.weights?.dryWeight}     valB={dB.weights?.dryWeight}     unit="kg" />
              <CompareRow label={t('field_fuel_liters')}    valA={dA.weights?.fuelLiters}    valB={dB.weights?.fuelLiters}    unit="L" />
              <CompareRow label={t('field_total_weight')}   valA={dA.weights?.totalWeight}   valB={dB.weights?.totalWeight}   unit="kg" />
              <CompareRow label={t('field_weight_dist')}    valA={dA.weights?.weightRip}     valB={dB.weights?.weightRip}     unit="%" />
              <CompareRow label={t('field_ballast_applied')} valA={dA.weights?.ballast}      valB={dB.weights?.ballast}       unit="kg" />

              {/* ══ STRATEGIA ══ */}
              <SectionHeader label={t('cc_tab_strategy')} />
              <CompareRow label={t('field_strategy_fuel_start')}       valA={dA.strategia?.carburanteIniziale}   valB={dB.strategia?.carburanteIniziale}   unit="L" />
              <CompareRow label={t('field_strategy_fuel_consumption')} valA={dA.differenziale?.fuelConsumption} valB={dB.differenziale?.fuelConsumption}  unit="L/lap" />
              <CompareRow label={t('field_strategy_brakes_front')}     valA={dA.freni?.pastiglieFrenoAnteriori} valB={dB.freni?.pastiglieFrenoAnteriori} />
              <CompareRow label={t('field_strategy_brakes_rear')}      valA={dA.freni?.pastiglieFrenoPosteriori} valB={dB.freni?.pastiglieFrenoPosteriori} />

              {/* ══ NOTE ══ */}
              <SectionHeader label={t('cc_tab_notes')} />
              <CompareRow label={t('field_best_lap')}       valA={dA.feedback?.bestLap}       valB={dB.feedback?.bestLap} />
              <CompareRow label={t('field_tech_notes')}     valA={dA.feedback?.noteTecnico}   valB={dB.feedback?.noteTecnico} />
              <CompareRow label={t('field_driver_feedback')} valA={dA.feedback?.feedbackPilota} valB={dB.feedback?.feedbackPilota} />

            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

export default ComparePage;
